// ============================================================
// DashboardView.tsx
// Stage C: shell + wired sections. The app's landing view, now
// fetching real data on mount via a colocated useDashboardData hook.
//
// Fetch strategy: ONE Promise.all on mount, four to six calls in
// parallel (four for non-admins, six for admins — the extra two
// slices power the ACROSS THE FIRM strip). Each slice has its own
// try/catch inside the Promise.all so a single failing endpoint
// leaves the rest of the dashboard functional — matches the "no
// blocking spinner, granular graceful degradation" model from spec.
//
// Sections wired this stage:
//   - WHAT NEEDS YOU: Overdue / Due Soon / Ready hero cards.
//   - YOUR NEXT DEADLINES: personal deadlines in the next 7 days
//     (calendar entries cross-referenced against my task list + my
//     assignee cases, since the backend calendar isn't yet
//     assignee-filterable server-side).
//   - JUMP BACK IN: my open cases, sorted by myLastViewedAt desc.
//   - ACROSS THE FIRM (admin-only): Open Cases / Due This Week /
//     Fee-earners. Recent Activity intentionally stays a placeholder
//     until the /api/audit-logs relax lands (see comment in FirmStrip).
//   - BILLABLE TIME: <BillableTimeCard> (own fetch, week paging).
//
// Pre-deploy graceful degradation: the assignee-filtered fetchCases
// call would 500 on the backend if Reuben's Prisma push hasn't
// landed (the column doesn't exist yet). We swallow that failure at
// the slice level; the READY hero and JUMP BACK IN cards fall back
// to their em-dash / empty-state placeholders. Detection is via
// promise rejection, not response-shape sniffing — a network 500 and
// a "column missing" 500 look identical to axios, and the UX for
// both is the same (fall back).
//
// Colocated hook: useDashboardData lives at the top of this file
// (not in src/hooks) because it has a single consumer and pairing
// the fetch code with the render code makes review easier.
// ============================================================
import { isTaskOverdue, isTaskDueSoon, ymdLocal, startOfLocalDay } from '../components/tasks/myWorkLogic'
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { goBack, initialPlaceParam, navigateTo, navigateWithin, usePlaceParam } from '../navigation/navigate'
import {
  fetchCases,
  listTasksApi,
  fetchCalendarApi,
  listUsersApi,
  listMyOverdueDeadlinesApi,
  countCasesMissingLimitationApi,
} from '../api/casevoApi'
import type { RawCase } from '../types/case.types'
import type { OverdueDeadline } from '../types/deadlines.types'
import type { Task } from '../types/tasks.types'
import type { CalendarEntry } from '../types/calendar.types'
import type { FirmUser } from '../types/user.types'
import { routeByItemType } from '../utils/routeByItemType'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import { SidebarHamburger } from '../components/layout/Sidebar'
import { caseStatusKey } from '../utils/caseStatus'
import { relativeTime } from '../utils/relativeTime'
import { daysUntilLocal } from '../utils/daysUntilLocal'
import { flushAutoSave } from '../hooks/useAutoSave'
import { flushClientAutoSave } from '../hooks/useClientAutoSave'
import { flushClientNotesAutoSave } from '../hooks/useClientNotesAutoSave'
import { flushWitnessAutoSave } from '../hooks/useWitnessAutoSave'
import { flushDeadlineAutoSave } from '../hooks/useCaseDeadlineAutoSave'
import DaySummaryCard from '../components/shared/DaySummaryCard'
import { presence } from '../services/presence'
import { useFirmChange } from '../utils/liveEvents'
import DashboardHero from '../components/dashboard/DashboardHero'
import BillableTimeCard from '../components/dashboard/BillableTimeCard'
import OverdueDeadlinesCard from '../components/dashboard/OverdueDeadlinesCard'
import { NO_LIMITATION_PARAM, NO_LIMITATION_VALUE } from '../utils/missingLimitation'
import { NextDeadlinesCard, FirmPanel, DeadlineRowData } from '../components/dashboard/DashboardPanels'
import { caseName, caseClientName, caseRef } from '../utils/caseName'
import { entryDayKey } from '../components/calendar/CalendarTimeGrid'

// ── Date helpers ─────────────────────────────────────────────
// ISO of "now" and "now + 7 days" at millisecond precision. The
// backend calendar `?from` / `?to` filters do inclusive Date
// comparisons after `new Date(<iso>)` parses; ISO strings from
// toISOString() round-trip cleanly. Millisecond boundary is fine
// for a dashboard read — no timezone plumbing needed.
const nowISO = (): string => new Date().toISOString()
const inNDaysISO = (days: number): string => {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString()
}
// ★ Is a deadline/task entry "due" in the dashboard window? Not completed
// (a ticked case deadline / a done task), and on or after TODAY by the local-day
// rule the calendar + My Work use (entryDayKey: timed → local day, all-day →
// stored day), up to the +7d cutoff.
const inDeadlineWindow = (e: CalendarEntry, todayKey: string, cutoff: Date): boolean => {
  if (!e.date) return false
  if (e.type === 'case_deadline' && e.status === 'completed') return false
  if (e.type === 'task' && e.status === 'done') return false
  return entryDayKey(e) >= todayKey && new Date(e.date).getTime() <= cutoff.getTime()
}

// Time-of-day greeting. Local browser time — no timezone plumbing
// needed for a landing pleasantry.
const getTimeOfDay = (): 'morning' | 'afternoon' | 'evening' => {
  const h = new Date().getHours()
  if (h < 12) return 'morning'
  if (h < 18) return 'afternoon'
  return 'evening'
}

// "Opened 3h ago" for JumpCard's timestamp label. Wraps the shared
// relativeTime util so the caption reads as an activity phrase, not
// just a bare relative time.
const openedRelative = (iso: string | null): string => {
  if (!iso) return 'Opened —'
  const rel = relativeTime(iso)
  return `Opened ${rel.charAt(0).toLowerCase()}${rel.slice(1)}`
}

// ── "Jump back in" day-stepper helpers (Callum #3) ──────────
// dayOffset semantics: 0 = today, -1 = yesterday, -2 = two days ago, …
// (never positive — you can't open cases in the future). All bounds are
// computed at LOCAL midnight then serialised via toISOString(), so the
// window sent to the backend brackets the user's LOCAL day, not UTC —
// the same setHours(0,0,0,0) discipline daysUntilLocal
// use to dodge the BST/DST off-by-one.
const dayBoundsLocal = (dayOffset: number): { from: string; to: string; date: Date } => {
  const from = new Date()
  from.setHours(0, 0, 0, 0)             // local midnight, today
  from.setDate(from.getDate() + dayOffset) // shift to the target day (offset ≤ 0)
  const to = new Date(from)
  to.setHours(23, 59, 59, 999)          // …end of that local day (inclusive)
  return { from: from.toISOString(), to: to.toISOString(), date: from }
}

// Friendly heading for the selected day. Recent days get words
// ("Today" / "Yesterday"); older days get a short local date
// ("Tue 3 Jun"). `date` is the local-midnight Date from dayBoundsLocal.
// "Thu 24 Sep" — built by hand so newer ICU's "Sept" never appears.
const shortDayLabel = (d: Date): string =>
  `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' }).slice(0, 3)}`

const dayHeadingLabel = (dayOffset: number, date: Date): string => {
  if (dayOffset === 0) return 'Today'
  if (dayOffset === -1) return 'Yesterday'
  return shortDayLabel(date)
}

// Offset (≤ 0) of a picked local-midnight date relative to today, in
// whole days. Used by the date-jump input. Mirrors daysUntilLocal's
// normalise-both-to-local-midnight-then-diff approach.
const offsetForLocalDate = (picked: Date): number => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const p = new Date(picked)
  p.setHours(0, 0, 0, 0)
  return Math.round((p.getTime() - today.getTime()) / 86400000)
}

// ============================================================
// useDashboardData — colocated single-consumer hook.
//
// Fires all slices in parallel on mount. Each slice's promise is
// wrapped in a `.then/.catch` block that resolves to a discriminated
// { ok, data } | { ok, error } — so Promise.all NEVER rejects, and
// a failing endpoint only affects its own slice. Idiomatic in-hook
// alternative to Promise.allSettled with cleaner destructuring.
//
// Slices:
//   a. myAssignedCases — fetchCases({ assignedToUserId: 'me', status: 'open' })
//                        Feeds READY (count of my open cases) and
//                        personalDeadlines (case-deadline cross-ref:
//                        "your next deadlines" needs deadlines on
//                        cases assigned to you, not cases you merely
//                        clicked on).
//   (JUMP BACK IN is no longer a batch slice — <JumpBackInCard> owns
//    its own per-day, all-status fetch; see Callum #3 day-stepper.)
//   c. overdueTasks    — listTasksApi({ assignedToUserId: 'me', dueTo: <now> }) → isTaskOverdue
//   d. openTasks       — listTasksApi({ status: 'open' })
//   e. calendar        — fetchCalendarApi({ from: <now>, to: <now+7d> })
//                        (personal narrow happens client-side —
//                        backend calendar isn't assignee-filterable)
//   f. firm strip: admin-only fetchCases({}) + listUsersApi()
//
// Returns per-slice { data, error } (no per-slice loading — one
// `isLoading` flag covers the whole batch, since we launch and await
// them all together). This keeps consumer code simple; if we ever
// want per-slice loading indicators, promote to a reducer.
// ============================================================
interface DashboardData {
  isLoading: boolean
  // Personal (all users)
  //
  // myAssignedCases is the count/list of open cases where the caller is
  // the assignee. (The former myRecentCases "recently opened" slice was
  // removed — <JumpBackInCard> now fetches its own per-day, all-status
  // data, so there's no shared recent-viewed slice to drift and re-inflate
  // the READY count.)
  myAssignedCases: { data: RawCase[] | null; error: string | null }
  overdueTasks: { data: Task[] | null; error: string | null }
  openTasks: { data: Task[] | null; error: string | null }
  calendar: { data: CalendarEntry[] | null; error: string | null }
  // Admin-only (undefined for non-admins so consumer code can short-
  // circuit on `if (firmCases.data)` without special-casing role).
  firmCases: { data: RawCase[] | null; error: string | null }
  firmUsers: { data: FirmUser[] | null; error: string | null }
  // Round 2 — my OVERDUE deadlines (any age) for the danger Overdue section.
  overdueDeadlines: { data: OverdueDeadline[] | null; error: string | null }
  // ★ Open cases I can see with no limitation-type deadline (the amber card).
  missingLimitation: { data: number | null; error: string | null }
}

const emptySlice = { data: null, error: null }

const useDashboardData = (isAdmin: boolean): DashboardData => {
  const [state, setState] = useState<DashboardData>({
    isLoading: true,
    myAssignedCases: emptySlice,
    overdueTasks: emptySlice,
    openTasks: emptySlice,
    calendar: emptySlice,
    firmCases: emptySlice,
    firmUsers: emptySlice,
    overdueDeadlines: emptySlice,
    missingLimitation: emptySlice,
  })

  // ★ (Batch C) LIVE refresh. The tiles / hero / deadlines were one-shot
  // (fetched on mount only), so a colleague assigning me a case, completing a
  // task or adding a deadline didn't show until I navigated away and back.
  // Any remote change to an entity the dashboard derives from → a debounced
  // SILENT re-run of the same fetch (no skeleton — isLoading stays false).
  const [liveNonce, setLiveNonce] = useState(0)
  useEffect(() => {
    let timer: number | undefined
    const unsub = presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'case' && e.entity !== 'task' && e.entity !== 'deadline' && e.entity !== 'appointment') return
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setLiveNonce((n) => n + 1), 1500)
    })
    return () => { unsub(); window.clearTimeout(timer) }
  }, [])
  // (D12 live updates) Firm lists the dashboard derives from changed (incl.
  // the actor's other tabs, which entity-changed skips) → the same silent re-run.
  useFirmChange(['cases', 'tasks', 'calendar', 'billing', 'users'], () => setLiveNonce((n) => n + 1))

  useEffect(() => {
    let cancelled = false
    // ★ Deadlines window starts at the START OF TODAY (local) — so today's
    // all-day items (stored at midnight UTC, i.e. already "before now") count.
    // End boundary unchanged: now + 7 days.
    const from = startOfLocalDay(new Date()).toISOString()
    const to = inNDaysISO(7)
    const now = nowISO()

    // Per-slice wrapper: resolve to { data } on success, { error } on
    // failure. Never rejects — so the outer Promise.all never rejects.
    const wrap = <T,>(p: Promise<T>): Promise<{ data: T | null; error: string | null }> =>
      p
        .then((data) => ({ data, error: null as string | null }))
        .catch((err: unknown) => ({
          data: null,
          error: err instanceof Error ? err.message : 'Request failed',
        }))

    // The dashboard's calls run in parallel — one round-trip. The order
    // matches the destructuring below. Non-admins skip the firm-wide cases
    // and users calls (`skip` — an empty slice, so the tuple shape stays
    // identical and destructuring is uniform).
    // myAssignedCases is the single fetchCases slice here (slot 0). The
    // "recently opened" cases are no longer batch-fetched: <JumpBackInCard>
    // owns its own per-day, all-status fetch (Callum #3 day-stepper), so
    // there's no myRecentCases slice to keep in sync.
    const skip = Promise.resolve(emptySlice as { data: null; error: null })

    void Promise.all([
      wrap(fetchCases({ assignedToUserId: 'me', status: 'open' })),
      // Bug-3 personal scoping: both task fetches pass
      // assignedToUserId: 'me' so admin users see their OWN OVERDUE
      // + DUE SOON counts (matching the "What needs you" section
      // header), not firm-wide. For non-admins this is a no-op —
      // taskController.getTasks L60-61 already server-forces
      // `where.assignedToUserId = userId` regardless. For admins it
      // narrows the query from firm-wide to personal, resolving the
      // sentinel to req.user.userId (taskController.getTasks L64-66).
      // Overdue candidates: my audience (same 'me' list as My work → Mine),
      // any status, due up to now; overdueCount below applies My work's exact
      // rule (isTaskOverdue) so the card == the filtered list it opens.
      wrap(listTasksApi({ assignedToUserId: 'me', dueTo: now })),
      // My ACTIVE tasks (open + in progress; done filtered out below) — was
      // status:'open' only, which dropped in-progress tasks from "next deadlines".
      wrap(listTasksApi({ assignedToUserId: 'me' }).then((ts) => ts.filter((t) => t.status !== 'done'))),
      wrap(fetchCalendarApi({ from, to })),
      isAdmin ? wrap(fetchCases({})) : skip,
      isAdmin ? wrap(listUsersApi()) : skip,
      wrap(listMyOverdueDeadlinesApi()),
      // MY cases only (owner OR handling team) — the hero's count. ★ The number
      // only (?count=1): the dashboard never showed the rows.
      wrap(countCasesMissingLimitationApi({ assignedToUserId: 'me' })),
    ]).then(([myAssignedCases, overdueTasks, openTasks, calendar, firmCases, firmUsers, overdueDeadlines, missingLimitation]) => {
      if (cancelled) return
      setState({
        isLoading: false,
        myAssignedCases,
        overdueTasks,
        openTasks,
        calendar,
        firmCases,
        firmUsers,
        overdueDeadlines,
        missingLimitation,
      })
    })

    return () => {
      cancelled = true
    }
    // Fires once on mount. isAdmin changing mid-session would require
    // a full re-fetch to expose/hide the admin slices — that's fine
    // because a role change forces a fresh JWT anyway (see S1 login).
  }, [isAdmin, liveNonce])

  return state
}

// ── Day-picker calendar popover ─────────────────────────────
// A small month-grid calendar for the "Jump back in" stepper — replaces
// the confusing native <input type="date"> (typing DD/MM/YYYY). Clickable
// days (no typing), FUTURE DAYS DISABLED, month nav (next capped at the
// current month — no future months), today + selected highlighted. Closes
// on outside-click / Escape / picking a day. No reusable month-grid
// existed in the app (CalendarView is an agenda list; DateInput is the
// three-box input), so this is purpose-built but styled with casevo-*
// tokens. Outside-click + Escape mirror CaseContextMenu; excludeRef keeps
// a click on the trigger button from both closing (outside) and
// re-toggling (NotificationsPanel's pattern).
const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] // Mon-first
interface DayCalendarPopoverProps {
  selectedDate: Date // local-midnight of the currently-selected day
  onPick: (d: Date) => void
  onClose: () => void
  excludeRef: React.RefObject<HTMLElement | null>
}
const DayCalendarPopover: React.FC<DayCalendarPopoverProps> = ({
  selectedDate,
  onPick,
  onClose,
  excludeRef,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  // Today at LOCAL midnight — the future cutoff + "today" highlight.
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  // The month on show — 1st of the selected day's month initially.
  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const d = new Date(selectedDate)
    d.setDate(1)
    d.setHours(0, 0, 0, 0)
    return d
  })

  // Close on outside-click + Escape (mirrors CaseContextMenu). The
  // excludeRef (the trigger button) is treated as "inside" so clicking it
  // to close doesn't fire outside-close AND the button's toggle.
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node
      if (ref.current && ref.current.contains(t)) return
      if (excludeRef.current && excludeRef.current.contains(t)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose, excludeRef])

  // Grid cells: leading blanks so the 1st lands under its weekday
  // (Mon-first), then one Date per day of the month.
  const cells = useMemo<(Date | null)[]>(() => {
    const year = viewMonth.getFullYear()
    const month = viewMonth.getMonth()
    const firstDow = (new Date(year, month, 1).getDay() + 6) % 7 // Mon=0 … Sun=6
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const arr: (Date | null)[] = []
    for (let i = 0; i < firstDow; i++) arr.push(null)
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d))
    return arr
  }, [viewMonth])

  // Next-month nav allowed only while the shown month precedes the current
  // month — no browsing into the future.
  const currentMonthStart = useMemo(() => {
    const d = new Date(today)
    d.setDate(1)
    return d
  }, [today])
  const canGoNextMonth = viewMonth.getTime() < currentMonthStart.getTime()

  const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  const monthNavClass =
    'w-6 h-6 flex items-center justify-center rounded text-casevo-mid ' +
    'hover:text-casevo-forest hover:bg-casevo-mint/40 transition-colors ' +
    'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent ' +
    'focus:outline-none casevo-focus text-sm leading-none'

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Choose a day"
      className="absolute right-0 top-full mt-1 z-20 w-64 p-3 rounded-lg border
                 border-casevo-border bg-casevo-surface shadow-lg"
    >
      {/* Month header with ‹ › */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() =>
            setViewMonth((m) => {
              const n = new Date(m)
              n.setMonth(n.getMonth() - 1)
              return n
            })
          }
          className={monthNavClass}
        >
          ‹
        </button>
        <span className="text-xs font-medium text-casevo-forest">
          {viewMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          aria-label="Next month"
          disabled={!canGoNextMonth}
          onClick={() =>
            setViewMonth((m) => {
              const n = new Date(m)
              n.setMonth(n.getMonth() + 1)
              return n
            })
          }
          className={monthNavClass}
        >
          ›
        </button>
      </div>
      {/* Weekday initials (Mon-first) */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAY_INITIALS.map((w, i) => (
          <div key={i} className="text-[10px] text-center text-casevo-mid uppercase">
            {w}
          </div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`blank-${i}`} />
          const isFuture = d.getTime() > today.getTime()
          const isToday = sameDay(d, today)
          const isSelected = sameDay(d, selectedDate)
          const cellClass = [
            'w-8 h-8 text-xs rounded flex items-center justify-center transition-colors',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-casevo-brand',
            isSelected
              ? 'bg-casevo-primary text-white font-semibold'
              : isToday
                ? 'border border-casevo-brand text-casevo-forest'
                : isFuture
                  ? 'text-casevo-mid/40 cursor-not-allowed'
                  : 'text-casevo-forest hover:bg-casevo-mint/40',
          ].join(' ')
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={isFuture}
              onClick={() => onPick(d)}
              aria-current={isToday ? 'date' : undefined}
              aria-label={d.toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
              className={cellClass}
            >
              {d.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── JUMP BACK IN — day-stepper card (Callum #3), mockup option 2 ─────
// Browse the cases YOU opened, one LOCAL day at a time (all statuses —
// "what I looked at that day"). Same per-day fetch, stepper, calendar
// popover and openCase behaviour as before; restyled to the approved
// mockup: 22px card padding · 16px gaps · header label + ‹ day › ·
// each case as the caselist's forest chip (radius 14 · pad 16/18 ·
// 135° #1a3a2a→#2d6a4f · dark #1b2e24→#223c2e + inset ring) · a faded
// "earlier days" list below.
//
// EARLIER DAYS are fetched LAZILY: only after the selected day has
// painted, deferred to browser idle time (requestIdleCallback, 400ms
// timeout fallback), as ONE request covering the 7 days before the
// selected day. They never block the dashboard's first paint. Clicking
// an earlier row steps the card to that day.
interface JumpBackInCardProps {
  openCase: (caseId: string) => void | Promise<void>
}
interface EarlierRow { offset: number; label: string; title: string; ref: string }

const JUMP_ICON = {
  folder: '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
  clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  arrow: '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>',
  chevL: '<polyline points="15 18 9 12 15 6"/>',
  chevR: '<polyline points="9 18 15 12 9 6"/>',
}
const JIc: React.FC<{ d: string; size?: number }> = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: d }} />
)

const JumpBackInCard: React.FC<JumpBackInCardProps> = ({ openCase }) => {
  // Session rename overlay — a case renamed this session shows its NEW name
  // here immediately (this card fetches its own day rows into local state,
  // which applyCaseRename can't reach).
  const renamedCaseNames = useCasevoStore((s) => s.renamedCaseNames)
  const [dayOffset, setDayOffset] = useState(0) // 0 = today, negative = past
  const [dayData, setDayData] = useState<RawCase[] | null>(null)
  const [dayLoading, setDayLoading] = useState(true)
  const [dayError, setDayError] = useState<string | null>(null)

  const { date } = useMemo(() => dayBoundsLocal(dayOffset), [dayOffset])
  const heading = dayHeadingLabel(dayOffset, date)
  const isToday = dayOffset === 0

  // Selected day (all statuses, local-day window) AND the earlier-days strip,
  // fetched in PARALLEL and shown together. ★ NO LAYOUT SHIFT: the "Earlier
  // days" strip used to load idle-time AFTER the chips had painted, growing the
  // card (and the dashboard row) a beat later.
  const [earlierList, setEarlierList] = useState<RawCase[] | null>(null)
  useEffect(() => {
    let cancelled = false
    setDayLoading(true)
    setDayError(null)
    setDayData(null)
    setEarlierList(null)
    const { from, to } = dayBoundsLocal(dayOffset)
    const earlierRange = { from: dayBoundsLocal(dayOffset - 7).from, to: dayBoundsLocal(dayOffset - 1).to }
    void Promise.all([
      fetchCases({ from, to })
        .then((data) => { if (!cancelled) setDayData(data) })
        .catch(() => { if (!cancelled) setDayError('Could not load that day.') }),
      fetchCases(earlierRange)
        .then((list) => { if (!cancelled) setEarlierList(list) })
        .catch(() => { if (!cancelled) setEarlierList([]) }),
    ]).finally(() => { if (!cancelled) setDayLoading(false) })
    return () => { cancelled = true }
  }, [dayOffset])

  // Title shown for a case — the shared caseName() rule, with the live rename
  // overlay applied first (a session rename wins over the fetched row).
  const titleOf = useCallback((c: RawCase): { title: string; client: string } => {
    const client = caseClientName(c) || 'Unnamed client'
    const own = Object.prototype.hasOwnProperty.call(renamedCaseNames, c.id) ? renamedCaseNames[c.id] : c.caseName
    return { title: caseName({ ...c, caseName: own }), client }
  }, [renamedCaseNames])

  // Earlier days — the most-recent case per earlier day (up to 3).
  const earlier = useMemo<EarlierRow[] | null>(() => {
    if (!earlierList) return null
    const sorted = [...earlierList].sort((a, b) =>
      (b.myLastViewedAt ? new Date(b.myLastViewedAt).getTime() : 0) -
      (a.myLastViewedAt ? new Date(a.myLastViewedAt).getTime() : 0))
    const byDay = new Map<number, RawCase>()
    for (const c of sorted) {
      if (!c.myLastViewedAt) continue
      const off = offsetForLocalDate(new Date(c.myLastViewedAt))
      if (off >= dayOffset || byDay.has(off)) continue
      byDay.set(off, c) // most-recent case per earlier day
    }
    return Array.from(byDay.entries())
      .sort((a, b) => b[0] - a[0])
      .slice(0, 3)
      .map(([off, c]) => ({
        offset: off,
        label: dayHeadingLabel(off, dayBoundsLocal(off).date),
        title: titleOf(c).title,
        ref: caseRef(c),
      }))
  }, [earlierList, dayOffset, titleOf])

  // Sort by the user's own last-viewed desc (deterministic guard).
  const cases = useMemo<RawCase[]>(() => {
    const list = [...(dayData ?? [])]
    list.sort((a, b) =>
      (b.myLastViewedAt ? new Date(b.myLastViewedAt).getTime() : 0) -
      (a.myLastViewedAt ? new Date(a.myLastViewedAt).getTime() : 0))
    return list
  }, [dayData])

  const [calendarOpen, setCalendarOpen] = useState(false)
  const labelBtnRef = useRef<HTMLButtonElement>(null)

  const iconBtn =
    'w-8 h-8 rounded-[9px] border border-casevo-border bg-casevo-surface text-casevo-mid inline-flex items-center justify-center ' +
    'hover:border-casevo-mid hover:text-casevo-forest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ' +
    'disabled:hover:border-casevo-border disabled:hover:text-casevo-mid focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid'

  return (
    <section className="rounded-[14px] border border-casevo-border bg-casevo-surface p-[22px] flex flex-col gap-4 min-w-0
                        shadow-[0_1px_2px_rgba(16,40,28,0.05),0_2px_6px_rgba(16,40,28,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
      <div className="flex items-center gap-2">
        <h2 className="text-[11.5px] tracking-[0.14em] uppercase font-semibold text-casevo-brand dark:text-[#74c69d]">Jump back in</h2>
        <span className="flex-1" />
        <button type="button" aria-label="Previous day" onClick={() => setDayOffset((o) => o - 1)} className={iconBtn}>
          <JIc d={JUMP_ICON.chevL} size={15} />
        </button>
        <div className="relative">
          <button
            ref={labelBtnRef}
            type="button"
            onClick={() => setCalendarOpen((o) => !o)}
            aria-haspopup="dialog"
            aria-expanded={calendarOpen}
            aria-label={`Change day — currently ${heading}`}
            className="text-[13px] font-semibold text-casevo-forest px-1 hover:underline underline-offset-2 decoration-dotted focus:outline-none focus-visible:underline"
          >
            {heading}
          </button>
          {calendarOpen && (
            <DayCalendarPopover
              selectedDate={date}
              excludeRef={labelBtnRef}
              onClose={() => setCalendarOpen(false)}
              onPick={(d) => { setDayOffset(offsetForLocalDate(d)); setCalendarOpen(false) }}
            />
          )}
        </div>
        <button type="button" aria-label="Next day" disabled={isToday} onClick={() => setDayOffset((o) => Math.min(0, o + 1))} className={iconBtn}>
          <JIc d={JUMP_ICON.chevR} size={15} />
        </button>
      </div>

      {dayLoading ? (
        // Chip-shaped: the same box + text lines as a real chip (bars inside the
        // real line wrappers), so the first chip lands exactly where it sat.
        <div aria-busy="true" className="rounded-[14px] px-[18px] py-4 flex items-center gap-3.5 bg-[#e8f1ec] dark:bg-[#31443a]">
          <span className="w-[38px] h-[38px] rounded-[10px] bg-white/60 dark:bg-white/10 shrink-0" />
          <span className="flex-1 min-w-0 motion-safe:animate-pulse">
            <span className="block text-[15.5px] font-bold"><span className="inline-block align-middle h-3.5 w-40 rounded bg-white/70 dark:bg-white/10" /></span>
            <span className="block text-[11px]"><span className="inline-block align-middle h-2.5 w-28 rounded bg-white/70 dark:bg-white/10" /></span>
            <span className="flex items-center gap-[5px] text-[12px] mt-1.5"><span className="inline-block h-3 w-20 rounded bg-white/70 dark:bg-white/10" />{'​'}</span>
          </span>
        </div>
      ) : dayError ? (
        <p className="text-[13px] text-casevo-text-soft">Couldn&apos;t load your recent cases.</p>
      ) : cases.length === 0 ? (
        <div className="rounded-[14px] border border-dashed border-casevo-border px-[18px] py-5 text-[13px] text-casevo-text-soft">
          No cases opened on this day.
        </div>
      ) : (
        // Only scroll when there are more than 3 chips. A scroll box clips
        // its children's box-shadow to a hard rectangle (the square edge
        // seen at the chip corners). When it does scroll, bleed the box out
        // over the card padding (-mx/px 22px, pb/-mb 12px) so the shadow has
        // room to fade before it hits the clip edge; layout is unchanged.
        <div className={`flex flex-col gap-2.5 ${cases.length > 3
          ? 'max-h-[300px] overflow-y-auto -mx-[22px] px-[22px] pt-0.5 -mt-0.5 pb-3 -mb-3'
          : ''}`}>
          {cases.map((c) => {
            const { title, client } = titleOf(c)
            const ref = caseRef(c) // THE reference (SURNAME-YEAR-ID)
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { void openCase(c.id) }}
                className="relative overflow-hidden rounded-[14px] px-[18px] py-4 text-white text-left flex items-center gap-3.5 shrink-0
                           bg-[linear-gradient(135deg,#1a3a2a,#2d6a4f)] shadow-[0_6px_18px_rgba(10,25,17,0.22)]
                           dark:bg-[linear-gradient(135deg,#1b2e24,#223c2e)] dark:shadow-[0_2px_10px_rgba(0,0,0,0.3),inset_0_0_0_1px_rgba(255,255,255,0.06)]
                           hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(10,25,17,0.34)] transition-all
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-gold"
              >
                <span className="w-[38px] h-[38px] rounded-[10px] bg-white/[0.14] flex items-center justify-center shrink-0">
                  <JIc d={JUMP_ICON.folder} size={18} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15.5px] font-bold truncate">{title}</span>
                  <span className="block text-[11px] font-mono opacity-70 tracking-[0.05em] truncate">
                    REF {ref}{client && client !== title ? ` · ${client}` : ''}
                  </span>
                  <span className="flex items-center gap-[5px] text-[12px] opacity-80 mt-1.5">
                    <JIc d={JUMP_ICON.clock} size={12} /> {openedRelative(c.myLastViewedAt)}
                  </span>
                </span>
                <span className="opacity-80 shrink-0"><JIc d={JUMP_ICON.arrow} /></span>
              </button>
            )
          })}
        </div>
      )}

      {!dayLoading && earlier && earlier.length > 0 && (
        <div className="border-t border-casevo-border-soft pt-3.5 flex flex-col gap-2.5">
          {earlier.map((r) => (
            <button key={r.offset} type="button" onClick={() => setDayOffset(r.offset)}
              className="flex items-center gap-2.5 text-[13px] opacity-55 hover:opacity-90 transition-opacity text-left focus:outline-none focus-visible:opacity-90 focus-visible:underline">
              <span className="w-[74px] shrink-0 text-casevo-text-soft text-[12px]">{r.label}</span>
              <span className="text-casevo-forest flex-1 min-w-0 truncate">{r.title}</span>
              <span className="font-mono text-[11px] text-casevo-text-soft">{r.ref}</span>
            </button>
          ))}
          <div className="text-[11.5px] text-casevo-text-soft">Earlier days · use ‹ ›</div>
        </div>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────
const DashboardView: React.FC = () => {
  const authUser = useCasevoStore((s) => s.authUser)
  const timeOfDay = getTimeOfDay()
  const firstName = authUser?.firstName?.trim() || 'there'
  const isAdmin = authUser?.role === 'admin'
  // Today's date for the header subtitle — UK long form, no year
  // (e.g. "Friday 24 July"). Derived client-side from new Date(); no
  // fetch. en-GB gives day-before-month ordering.
  const todayLabel = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  // Today's LOCAL calendar day as 'YYYY-MM-DD' — the AI day-summary key.
  const todayKey = (() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })()

  const {
    isLoading,
    myAssignedCases,
    overdueTasks,
    openTasks,
    calendar,
    firmCases,
    firmUsers,
    overdueDeadlines,
    missingLimitation,
  } = useDashboardData(isAdmin)
  // Fingerprint of the fetched calendar window — changes only when an item is
  // added / removed / retimed / renamed / re-statused (drives the briefing's
  // dataVersion so it re-fetches after a real change, not on every event).
  const calendarFingerprint = useMemo(
    () => (calendar.data ?? []).map((e) => `${e.type}|${e.taskId ?? e.deadlineId ?? e.caseId ?? ''}|${e.date}|${e.title}|${e.status ?? ''}`).join('~'),
    [calendar.data],
  )
  // Only a CHANGE after the first load bumps the briefing (the first load's
  // fingerprint must not trigger a second summary fetch on mount).
  const [briefingVersion, setBriefingVersion] = useState('0')
  const firstFpRef = useRef<string | null>(null)
  useEffect(() => {
    if (isLoading) return
    if (firstFpRef.current === null) { firstFpRef.current = calendarFingerprint; return }
    if (calendarFingerprint !== firstFpRef.current) {
      firstFpRef.current = calendarFingerprint
      setBriefingVersion(calendarFingerprint)
    }
  }, [isLoading, calendarFingerprint])

  // ── Navigate-to-case helper ──────────────────────────────
  // Mirrors CalendarView L294-304's flush-then-open pattern:
  //   1. Flush all five autosave streams (belt-and-braces — no case
  //      currently open here, but symmetry with every other nav
  //      handler is more valuable than skipping a no-op flush).
  //   2. setCaseOpenedFrom('dashboard') so the case-detail Back
  //      button knows the return destination.
  //   3. closeAllTakeovers() turns showDashboard off FIRST, so
  //      ViewingPane shows its loading shell immediately.
  //   4. setCurrentCaseById(id) fetches + maps the case; ViewingPane
  //      swaps the skeleton for the case page.
  const openCase = useCallback(async (caseId: string): Promise<void> => {
    await Promise.all([
      flushAutoSave(),
      flushClientAutoSave(),
      flushClientNotesAutoSave(),
      flushWitnessAutoSave(),
      flushDeadlineAutoSave(),
    ])
    try {
      // The case page's loading shell — topbar + skeleton — shows at once
      // instead of the dashboard sitting frozen for the whole load. One
      // history step: Back returns to the dashboard.
      await navigateTo({ view: 'caseDetail', caseId }, { skipFlush: true, intents: (s) => s.setCaseOpenedFrom('dashboard') })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DashboardView] Failed to open case:', err)
    }
  }, [])

  // ── READY card → filtered Cases list ─────────────────────
  // Clicking READY opens the Cases list showing EXACTLY the cases the
  // READY count counted: my OPEN cases (assignee = me, status = open).
  // The list lazy-reads these two filters from localStorage on mount, so
  // we write them BEFORE navigating (write-then-navigate; CaseListScreen
  // is unmounted while the dashboard shows, so it re-reads on fresh
  // mount). recentOnly is forced FALSE via its store setter — its default
  // is true ("last 24h viewed"), which would make the landing list a
  // SUBSET of READY. status='open' already carries READY's draft
  // exclusion (getStatusKey → open requires setupFinalised && !closeDate),
  // so drafts stay out on both sides. Flush-then-navigate mirrors
  // Sidebar.handleCases + this file's openCase.
  const openReadyCases = useCallback(async (): Promise<void> => {
    await Promise.all([
      flushAutoSave(),
      flushClientAutoSave(),
      flushClientNotesAutoSave(),
      flushWitnessAutoSave(),
      flushDeadlineAutoSave(),
    ])
    try {
      // The Cases list on its "All" tab (recentOnly off) — one history step.
      // ★ (Audit) Status + assignee ride in the link as ONE-SHOT params: the
      // list applies them for this visit without overwriting the user's
      // remembered filters (CaseListScreen).
      await navigateTo({ view: 'caseList', listTab: 'all', params: { status: 'open', assignee: 'me' } }, { skipFlush: true })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DashboardView] Failed to open READY cases:', err)
    }
  }, [])

  // ── "N cases with no limitation date" (hero link) ────────────
  // GET /api/cases/missing-limitation?assignedToUserId=me — open cases (drafts
  // included) with no limitation-type deadline that are MINE (owner OR
  // handling team), for admins too: the case list's own "me" rule, so the
  // count equals the rows the link opens.
  const noLimitationCount = missingLimitation.data ?? 0
  // The link → Cases with assignee "me" + the "No limitation date" status
  // option (?limitation=missing); status "all" (drafts count — the list forces
  // it on that filter). ★ One-shot params: the user's remembered filters are
  // left alone. One history step (Back returns here).
  const openMissingLimitationCases = useCallback(async (): Promise<void> => {
    try {
      await navigateTo({ view: 'caseList', listTab: 'all', params: { [NO_LIMITATION_PARAM]: NO_LIMITATION_VALUE, assignee: 'me' } })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DashboardView] Failed to open cases with no limitation date:', err)
    }
  }, [])

  // ── OVERDUE card → Tasks view with the OVERDUE filter ────────
  // Clicking OVERDUE opens the Tasks view showing EXACTLY the tasks the
  // OVERDUE count counted: my overdue tasks (My work's isTaskOverdue rule,
  // over the same 'me' audience). TasksView reads pendingTasksOverdue on
  // mount and starts on Mine with its Overdue filter ON — the same rule
  // over the same list — so the landing list's count matches the card. Write-then-navigate
  // (TasksView is unmounted while the dashboard shows, so it re-reads
  // the flag on fresh mount). Flush mirrors openReadyCases + Sidebar
  // handleTasks; navigation is closeAllTakeovers() + setShowTasks(true),
  // exactly Sidebar.handleTasks.
  const openOverdueTasks = useCallback(async (): Promise<void> => {
    await Promise.all([
      flushAutoSave(),
      flushClientAutoSave(),
      flushClientNotesAutoSave(),
      flushWitnessAutoSave(),
      flushDeadlineAutoSave(),
    ])
    try {
      await navigateTo({ view: 'tasks' }, { skipFlush: true, intents: (s) => s.setPendingTasksOverdue(true) })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DashboardView] Failed to open OVERDUE tasks:', err)
    }
  }, [])

  // ── DUE SOON "N tasks" → Tasks view with the "Due in 7 days" window ──
  // Same hand-off shape as OVERDUE: a one-shot store flag TasksView reads on
  // mount (My work opens on Mine with due today → +7 days, open work only).
  const openDueSoonTasks = useCallback(async (): Promise<void> => {
    try {
      await navigateTo({ view: 'tasks' }, { intents: (s) => s.setPendingTasksDueSoon(true) })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DashboardView] Failed to open DUE SOON tasks:', err)
    }
  }, [])

  // ── Derived counts / lists ──────────────────────────────
  // Everything below is a pure derivation over the per-slice data.
  // useMemo keeps them stable across re-renders that don't touch the
  // underlying arrays (e.g. authUser refresh).

  // OVERDUE = length of the overdue-task slice (backend already
  // filtered dueTo <= now && status = 'open'). null while loading /
  // errored — so the hero card renders em-dash rather than 0 in
  // those cases.
  const overdueCount = useMemo<number | null>(() => {
    if (isLoading) return null
    if (overdueTasks.error) return null
    // Same definition as My work's Overdue tile/filter (open OR in progress;
    // timed → past the instant; all-day → before today, local).
    const now = new Date()
    return (overdueTasks.data ?? []).filter((t) => isTaskOverdue(t, now)).length
  }, [isLoading, overdueTasks])

  // DUE SOON is defined below alongside personalDeadlines — both
  // derive from personalDeadlinesAll so the hero count and the list
  // stay in lockstep. Bug-2 fix; see the docblock above
  // personalDeadlinesAll for the semantic reasoning.

  // READY = my open cases count (from the assignee-filtered slice).
  // Errored → null (pre-deploy path). This is the ONLY hero whose
  // "unknown" state might not be a transient blip — it might be the
  // Reuben-hasn't-pushed state. Handled uniformly: em-dash + subtitle
  // unchanged (spec: "subtitle stays 'Open cases assigned to you'").
  //
  // DRAFT EXCLUSION: the backend `?status=open` filter is buggy —
  // it checks only closeDate (see caseController.ts L28/L76) and so
  // returns drafts + opens together. `setupFinalised === false`
  // means the case is still in the setup wizard (draft); those must
  // not count as "ready to work on". Filtering client-side keeps
  // this correct regardless of whether the backend filter is later
  // fixed. Closed cases were already excluded server-side (their
  // closeDate is non-empty), so the only additional guard needed
  // here is `setupFinalised`.
  const readyCount = useMemo<number | null>(() => {
    if (isLoading) return null
    if (myAssignedCases.error) return null
    const list = myAssignedCases.data ?? []
    return list.filter((c) => c.setupFinalised).length
  }, [isLoading, myAssignedCases])

  // ── YOUR NEXT DEADLINES ────────────────────────────────
  // Personal-scoped list. The backend calendar returns EVERYTHING
  // firm-wide (assignee filter isn't wired server-side); we filter
  // client-side by cross-referencing:
  //   - task entries         → keep if taskId in my openTasks list
  //   - case_deadline entries → ALWAYS keep (Fix B).
  //
  // Fix B (case-deadline visibility): case_deadline entries used
  // to require the case be in myAssignedCases. That hid firm-wide
  // deadlines from users whose cases weren't yet assigned to them
  // — a common shape in a small-firm workflow where a Trial date
  // is set on a case that hasn't been assigned yet (or is being
  // covered by whoever's around). The product decision is to
  // ALWAYS show firm case_deadlines on the personal dashboard;
  // task deadlines stay personally-narrowed (server auto-scopes
  // to the caller for non-admins and Bug-3 wired assignedToUserId:
  // 'me' for admins). Because case_deadlines no longer cross-ref
  // any case slice, myAssignedCases is no longer read here — the
  // dep and the intermediate Set have been dropped accordingly.
  //
  // Bug-2 split: personalDeadlinesAll holds the FULL window (no
  // slice cap); the top-4 slice for the list and the length for
  // the DUE SOON hero both derive from it, guaranteeing the count
  // and the list stay in lockstep.
  //
  // Date window: the calendar fetch already narrows to [now, now+7d]
  // via ?from/?to (see useEffect L138-139), so the entries array is
  // already the right window. The explicit date filter below is
  // belt-and-braces — matches the app's existing date-window idiom
  // (compare `d.getTime()` against `cutoff.getTime()` / `Date.now()`,
  // same pattern the pre-Bug-2 dueSoonCount used and that
  // CalendarView reuses for date grouping).
  const personalDeadlinesAll = useMemo<CalendarEntry[]>(() => {
    if (isLoading) return []
    if (calendar.error) return []
    const myTaskIds = new Set((openTasks.data ?? []).map((t) => t.id))
    const entries = calendar.data ?? []
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() + 7)
    const todayKey = ymdLocal(new Date())
    const personal = entries.filter((e) => {
      // Task deadlines still cross-ref against my task set (server
      // auto-scopes for non-admins; Bug-3 wired 'me' for admins).
      // Case deadlines pass unconditionally per Fix B — every firm
      // case_deadline in the window shows on every user's dashboard.
      const isKept =
        (e.type === 'task' && e.taskId ? myTaskIds.has(e.taskId) : false) ||
        (e.type === 'case_deadline' && !!e.caseId)
      if (!isKept) return false
      return inDeadlineWindow(e, todayKey, cutoff)
    })
    // Sort ascending by date — earliest first. Same convention as
    // CalendarView's date-grouping.
    personal.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    return personal
  }, [isLoading, calendar, openTasks])

  // Top-4 slice for the "Your next deadlines" panel list. Derived
  // from personalDeadlinesAll so the list stays in lockstep with
  // the DUE SOON hero count (both consume the same memo).
  const personalDeadlines = useMemo<CalendarEntry[]>(
    () => personalDeadlinesAll.slice(0, 4),
    [personalDeadlinesAll],
  )

  // DUE SOON — two counts, each matching the place its click opens:
  //   tasks     — my active tasks (lead / co-assignee / firm-wide: the same
  //               'me' list as My work → Mine) due today → +7 days, by
  //               isTaskDueSoon — the SAME rule as Tasks' "Due in 7 days".
  //   deadlines — the firm's open case deadlines in the window (Fix B), i.e.
  //               the case_deadline rows of personalDeadlinesAll — exactly
  //               the rows "Your next deadlines" lists in deadlines-only mode.
  // Null while loading / when that slice errored (em-dash, not 0).
  const dueSoonTasksCount = useMemo<number | null>(() => {
    if (isLoading || openTasks.error) return null
    const now = new Date()
    return (openTasks.data ?? []).filter((t) => isTaskDueSoon(t, now)).length
  }, [isLoading, openTasks])
  const dueSoonDeadlines = useMemo<CalendarEntry[]>(
    () => personalDeadlinesAll.filter((e) => e.type === 'case_deadline'),
    [personalDeadlinesAll],
  )
  const dueSoonDeadlinesCount = isLoading || calendar.error ? null : dueSoonDeadlines.length
  // "N deadlines" on the tile → "Your next deadlines" narrowed to just those
  // (every one, no top-4 cap), scrolled into view + focused.
  const [deadlinesOnly, setDeadlinesOnly] = useState(false)
  const deadlinesRef = useRef<HTMLElement>(null)
  // "Due this week" (Across the firm) → the same list in FIRM-WEEK mode: every
  // firm deadline + task the tile counts (firmDueThisWeek below). It's a place
  // (?deadlines=firm-week): the URL shows it, a refresh keeps it, Back leaves it.
  const [firmWeek, setFirmWeek] = useState<boolean>(() => initialPlaceParam('deadlines') === 'firm-week')
  usePlaceParam('deadlines', firmWeek ? 'firm-week' : null, (v) => setFirmWeek(v === 'firm-week'))
  const focusDeadlines = useCallback((): void => {
    window.requestAnimationFrame(() => {
      const el = deadlinesRef.current
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      el.focus({ preventScroll: true })
    })
  }, [])
  // Arriving on ?deadlines=firm-week (refresh / deep link): bring the list into view.
  useEffect(() => { if (firmWeek) focusDeadlines() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const showDueSoonDeadlines = useCallback((): void => {
    setDeadlinesOnly(true)
    setFirmWeek(false)
    window.requestAnimationFrame(() => {
      const el = deadlinesRef.current
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      el.focus({ preventScroll: true })
    })
  }, [])

  // Urgency dot classifier for DeadlineRow.
  //
  // Fix A: switched from raw-millisecond diff against Date.now()
  // to daysUntilLocal, which normalises BOTH anchors to local
  // midnight before diffing (mirrors CalendarView.tsx L129-137).
  // Preserves the previous boundaries verbatim: "overdue" for
  // any strictly-past day, "soon" for the current 3-day window
  // (today + next 3 days), "later" beyond that. The pre-fix code
  // used `t < now + 3 * 24 * 60 * 60 * 1000` which — because of
  // the UTC-midnight vs local-instant mismatch — could flip a
  // "day 3 tomorrow" into "day 2 today" in BST. On the corrected
  // local-day basis the equivalent is `d <= 3`.
  const urgencyFor = (iso: string): 'overdue' | 'soon' | 'later' => {
    const d = daysUntilLocal(iso)
    if (d < 0) return 'overdue'
    if (d <= 3) return 'soon'
    return 'later'
  }

  // Compact "when" chip. "Today" / "Tomorrow" / "in 3d" / "4d ago".
  //
  // Fix A: same normalisation swap as urgencyFor. Label strings
  // are byte-identical to the pre-fix output for every valid
  // day-diff — only the underlying day count is now the correct
  // local-day count instead of the BST-off-by-one raw-ms count.
  const whenChip = (iso: string): string => {
    const d = daysUntilLocal(iso)
    if (d === 0) return 'Today'
    if (d === 1) return 'Tomorrow'
    if (d === -1) return 'Yesterday'
    if (d < 0) return `${Math.abs(d)}d ago`
    return `in ${d}d`
  }

  // ── JUMP BACK IN ─────────────────────────────────────────
  // Now rendered by <JumpBackInCard> (Callum #3 day-stepper), which owns
  // its own per-day fetch for EVERY day (today included) via
  // GET /api/cases?from&to — no status filter, so it shows ALL cases
  // opened that LOCAL day (open/closed/draft), a "what I looked at that
  // day" view. Per-user CaseAccess exists-filter, regardless of who
  // created/owns the case. The card handles sorting + skeleton loading +
  // empty states internally, so the former jumpBackCases memo and the
  // myRecentCases batch slice are both gone.

  // ── ACROSS THE FIRM (admin) ─────────────────────────────
  // OPEN CASES: firm-wide count of cases whose status is 'open'.
  // Case status is a THREE-STATE derivation over two schema columns
  // (backend/prisma/schema.prisma L68-70 — no `status` enum on Case;
  // `setupFinalised Boolean` + `closeDate String?` between them
  // express draft / open / closed):
  //   - draft  = !setupFinalised
  //   - open   = setupFinalised && (closeDate null or empty)
  //   - closed = setupFinalised && closeDate non-empty
  // Backend's `?status=open` filter (caseController.ts L28/L76) only
  // checks closeDate — it counts drafts + opens together as if they
  // were one bucket. That's a real bug (mirrored here pre-fix), but
  // fixing it server-side is out of scope for this pass; we bucket
  // correctly client-side over the unfiltered firmCases response.
  const firmOpenCasesCount = useMemo<number | null>(() => {
    if (!isAdmin || isLoading) return null
    if (firmCases.error) return null
    // The case list's OWN status rule (caseStatusKey) over the same unfiltered
    // fetch the list makes on its All tab — so this number == the rows the
    // tile opens (Cases › All, status Open, every assignee).
    return (firmCases.data ?? []).filter((c) => caseStatusKey(c) === 'open').length
  }, [isAdmin, isLoading, firmCases])

  // DUE THIS WEEK (admin, firm-wide): DEADLINES + TASKS only — no
  // appointments / reminders / Outlook events — excluding completed deadlines
  // and done tasks, over the same start-of-today → +7d window (local day).
  // The tile's number is this list's length, and the list it opens IS this
  // list — one source.
  const firmDueThisWeek = useMemo<CalendarEntry[]>(() => {
    if (!isAdmin || isLoading || calendar.error) return []
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() + 7)
    const todayKey = ymdLocal(new Date())
    return (calendar.data ?? [])
      .filter((e) => (e.type === 'case_deadline' || e.type === 'task') && inDeadlineWindow(e, todayKey, cutoff))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [isAdmin, isLoading, calendar])
  const firmDueThisWeekCount = !isAdmin || isLoading || calendar.error ? null : firmDueThisWeek.length

  // FEE-EARNERS: users where isActive === true.
  const feeEarnersCount = useMemo<number | null>(() => {
    if (!isAdmin || isLoading) return null
    if (firmUsers.error) return null
    return (firmUsers.data ?? []).filter((u) => u.isActive).length
  }, [isAdmin, isLoading, firmUsers])

  // ── Open calendar (deadlines "Open calendar →") ─────────────
  // Identical flush-then-navigate sequence to Sidebar.handleCalendar.
  const openCalendar = useCallback(async (): Promise<void> => {
    await Promise.all([flushAutoSave(), flushClientAutoSave(), flushClientNotesAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
    if (!(await navigateTo({ view: 'calendar' }, { skipFlush: true }))) return
    void useCasevoStore.getState().loadCalendarEntries()
  }, [])

  // Deadline rows for the table — same entries, same routing as before.
  // Deadlines-only mode (from the Due soon tile) lists every in-window case
  // deadline; otherwise the top 4 of tasks + deadlines.
  const listMode: 'firm-week' | 'deadlines' | 'mine' = firmWeek && isAdmin ? 'firm-week' : deadlinesOnly ? 'deadlines' : 'mine'
  const deadlineRows = useMemo<DeadlineRowData[]>(() => (listMode === 'firm-week' ? firmDueThisWeek : listMode === 'deadlines' ? dueSoonDeadlines : personalDeadlines).map((entry, i) => ({
    key: `${entry.type}-${entry.taskId ?? entry.caseId ?? i}`,
    title: entry.title,
    typeLabel: entry.type === 'task' ? 'Task' : 'Deadline',
    caseLabel: entry.caseLabel ?? (entry.type === 'task' ? (listMode === 'firm-week' ? 'Task (no case)' : 'Personal task') : ''),
    when: whenChip(entry.date),
    dateLabel: shortDayLabel(new Date(entry.date)),
    urgency: urgencyFor(entry.date),
    onClick: (entry.caseId || entry.taskId)
      ? () => { void routeByItemType(entry, 'dashboard') }
      : undefined,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [personalDeadlines, dueSoonDeadlines, firmDueThisWeek, listMode])

  // ── ACROSS THE FIRM tiles (admin) ───────────────────────────
  // Open cases → Cases › All, status Open, every assignee (the URL carries
  // ?status=open). Same flush-then-navigate as openReadyCases.
  const openFirmOpenCases = useCallback(async (): Promise<void> => {
    await Promise.all([flushAutoSave(), flushClientAutoSave(), flushClientNotesAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
    try {
      // One-shot params (the remembered filters are left alone).
      await navigateTo({ view: 'caseList', listTab: 'all', params: { status: 'open', assignee: 'all' } }, { skipFlush: true })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DashboardView] Failed to open open cases:', err)
    }
  }, [])
  // Due this week → the deadlines list in firm-week mode (a step inside the dashboard).
  const showFirmWeek = useCallback((): void => {
    setDeadlinesOnly(false)
    void navigateWithin(() => setFirmWeek(true)).then((ok) => { if (ok) focusDeadlines() })
  }, [focusDeadlines])
  // Fee-earners → Admin › Team.
  const openTeam = useCallback((): void => { void navigateTo({ view: 'admin', params: { section: 'team' } }) }, [])

  // ── Render ────────────────────────────────────────────
  // "Command centre" layout — the approved mockup (option 2), values
  // copied exactly: content column max 1140 · padding 40/44/56 · hero →
  // briefing floated −40px over its edge (inset 26px, raised shadow) →
  // [time 1.7fr | jump 1fr] gap 20, mt 26 → [deadlines 1.7fr | firm 1fr].
  // Mobile: everything stacks (hero → stats → cards).
  return (
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      <AppHeader leftHamburger={<SidebarHamburger />} leftSlot={<BackButton />} />
      <div className="max-w-[1180px] mx-auto leading-[normal] px-4 pt-6 pb-10 sm:px-8 lg:px-11 lg:pt-10 lg:pb-14">
        <DashboardHero
          dateLabel={todayLabel}
          greeting={`Good ${timeOfDay}, ${firstName}.`}
          overdue={overdueCount}
          dueSoonTasks={dueSoonTasksCount}
          dueSoonDeadlines={dueSoonDeadlinesCount}
          ready={readyCount}
          overdueError={!isLoading && !!overdueTasks.error}
          dueSoonError={!isLoading && !!calendar.error && !!openTasks.error}
          readyError={!isLoading && !!myAssignedCases.error}
          onOverdue={() => { void openOverdueTasks() }}
          onDueSoonTasks={() => { void openDueSoonTasks() }}
          onDueSoonDeadlines={showDueSoonDeadlines}
          onReady={() => { void openReadyCases() }}
          noLimitation={isLoading ? null : noLimitationCount}
          onNoLimitation={() => { void openMissingLimitationCases() }}
        />

        {/* AI briefing — floats over the hero's bottom edge. */}
        <div className="relative z-[2] -mt-10 mx-3 sm:mx-[26px]">
          <DaySummaryCard
            date={todayKey}
            variant="briefing"
            // (Batch C) re-fetch the briefing when the underlying calendar
            // window actually changes (live refresh above) — not on every event.
            dataVersion={briefingVersion}
            className="shadow-[0_10px_30px_-12px_rgba(16,40,28,0.25),0_2px_6px_rgba(16,40,28,0.06)] dark:shadow-[0_14px_34px_-14px_rgba(0,0,0,0.6)]"
          />
        </div>

        {/* Round 2 — OVERDUE deadlines: danger-styled, never dropped after a
            day; renders only when something is overdue. Loaded with the rest of
            the dashboard (useDashboardData), so it appears together with it. */}
        {!isLoading && (overdueDeadlines.data?.length ?? 0) > 0 && (
          <div className="mt-[26px]">
            <OverdueDeadlinesCard rows={overdueDeadlines.data} onOpenCase={(id) => { void openCase(id) }} />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-5 mt-[26px] mb-5">
          <BillableTimeCard />
          <JumpBackInCard openCase={openCase} />
        </div>

        <div className={`grid grid-cols-1 gap-5 ${isAdmin ? 'lg:grid-cols-[1.7fr_1fr]' : ''}`}>
          <NextDeadlinesCard
            rows={deadlineRows}
            totalCount={listMode === 'firm-week' ? firmDueThisWeek.length : listMode === 'deadlines' ? dueSoonDeadlines.length : personalDeadlinesAll.length}
            deadlinesOnly={listMode === 'deadlines'}
            firmWeek={listMode === 'firm-week'}
            onShowAll={() => setDeadlinesOnly(false)}
            onLeaveFirmWeek={() => goBack(() => { void navigateWithin(() => setFirmWeek(false), { replace: true }) })}
            sectionRef={deadlinesRef}
            isLoading={isLoading}
            error={!isLoading && !!calendar.error}
            onOpenCalendar={() => { void openCalendar() }}
          />
          {/* ACROSS THE FIRM — admin-only, unchanged data + gating. */}
          {isAdmin && (
            <FirmPanel
              openCases={firmOpenCasesCount}
              dueThisWeek={firmDueThisWeekCount}
              feeEarners={feeEarnersCount}
              // Admin-only panel; each tile opens exactly what it counts.
              onOpenCases={() => { void openFirmOpenCases() }}
              onDueThisWeek={showFirmWeek}
              onFeeEarners={isAdmin ? openTeam : undefined}
              errors={{ firmCases: !!firmCases.error, calendar: !!calendar.error, firmUsers: !!firmUsers.error }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
export default DashboardView
